import { AlertTriangle } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './Button.jsx';

const ConfirmContext = createContext(null);

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const useConfirm = () => {
  const confirm = useContext(ConfirmContext);
  if (!confirm) throw new Error('useConfirm must be used inside a ConfirmProvider.');
  return confirm;
};

export const ConfirmProvider = ({ children }) => {
  const [request, setRequest] = useState(null);
  const resolverRef = useRef(null);

  const confirm = useCallback(
    (options = {}) =>
      new Promise((resolve) => {
        resolverRef.current = resolve;
        setRequest(options);
      }),
    [],
  );

  const settle = useCallback((result) => {
    setRequest(null);
    const resolve = resolverRef.current;
    resolverRef.current = null;
    if (resolve) resolve(result);
  }, []);

  const value = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {request ? <ConfirmDialog request={request} onSettle={settle} /> : null}
    </ConfirmContext.Provider>
  );
};

const ConfirmDialog = ({ request, onSettle }) => {
  const { t } = useTranslation();
  const dialogRef = useRef(null);
  const previouslyFocused = useRef(null);
  const [typed, setTyped] = useState('');

  const {
    title,
    description,
    confirmLabel,
    cancelLabel,
    tone = 'danger',
    requireText = '',
  } = request;

  const confirmDisabled = Boolean(requireText) && typed.trim() !== requireText.trim();

  useEffect(() => {
    previouslyFocused.current = document.activeElement;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    const frame = window.requestAnimationFrame(() => {
      const node = dialogRef.current?.querySelector(requireText ? 'input' : '[data-confirm-default]');
      (node || dialogRef.current)?.focus();
    });

    return () => {
      window.cancelAnimationFrame(frame);
      document.body.style.overflow = overflow;
      if (previouslyFocused.current instanceof HTMLElement) previouslyFocused.current.focus();
    };
  }, [requireText]);

  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onSettle(false);
      return;
    }

    if (event.key !== 'Tab') return;

    const nodes = Array.from(dialogRef.current?.querySelectorAll(FOCUSABLE) || []);
    if (!nodes.length) return;

    const first = nodes[0];
    const last = nodes[nodes.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] grid place-items-center bg-beam-ink/60 p-4 backdrop-blur-sm"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onSettle(false);
      }}
    >
      <div
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        aria-describedby={description ? 'confirm-description' : undefined}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        className="w-full max-w-md rounded-lg bg-white p-5 shadow-crisp outline-none sm:p-6"
      >
        <div className="flex items-start gap-3">
          <span
            className={`grid h-11 w-11 shrink-0 place-items-center rounded-lg ${
              tone === 'danger' ? 'bg-state-critical/10 text-state-critical' : 'bg-beam-blue/10 text-beam-blue'
            }`}
          >
            <AlertTriangle className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h2 id="confirm-title" className="font-display text-2xl font-extrabold text-beam-ink">
              {title || t('common.confirmTitle')}
            </h2>
            {description ? (
              <p id="confirm-description" className="mt-2 text-sm font-semibold leading-6 text-beam-graphite/70">
                {description}
              </p>
            ) : null}
          </div>
        </div>

        {requireText ? (
          <label className="mt-5 grid gap-2 text-sm font-bold">
            {t('common.typeToConfirm', { value: requireText })}
            <input
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              autoComplete="off"
              className="focus-ring rounded-lg border border-beam-blue/10 px-4 py-3 font-mono text-sm outline-none"
            />
          </label>
        ) : null}

        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={() => onSettle(false)} className="w-full sm:w-auto">
            {cancelLabel || t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant={tone === 'danger' ? 'danger' : 'primary'}
            data-confirm-default
            disabled={confirmDisabled}
            onClick={() => onSettle(true)}
            className="w-full sm:w-auto"
          >
            {confirmLabel || t('common.delete')}
          </Button>
        </div>
      </div>
    </div>
  );
};
